/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { formatPattern } from 'react-router';

import PropTypes from 'prop-types';

import useRouter from 'js/lib/useRouter';

import { AIGenerateBrandedIcon, ArrowPreviousIcon } from '@coursera/cds-icons';

import { useIsAdminCoachV2Enabled } from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import { AdminOrgSwitcherPopup } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/AdminOrgSwitcherPopup';
import { AdminProfilePill } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/AdminProfilePill';
import { SideNav } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNav';
import { SideNavItem as SideNavItemComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavItem';
import type { SideNavItem } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import { useAdminSideNavItems } from 'bundles/enterprise-admin-navigation/components/side-nav/config/useAdminSideNavItems';
import { useSettingsSideNavItems } from 'bundles/enterprise-admin-navigation/components/side-nav/config/useSettingsSideNavItems';
import { useShouldShowAskCoursera } from 'bundles/enterprise-admin-navigation/components/side-nav/useShouldShowAskCoursera';
import { useViewAdminDashboardPageTracker } from 'bundles/enterprise-admin-navigation/components/side-nav/useViewAdminDashboardPageTracker';
import {
  ADMIN_SIDENAV_WIDTH_COLLAPSED_PX,
  ADMIN_SIDENAV_WIDTH_EXPANDED_PX,
} from 'bundles/enterprise-admin-navigation/constants';
import { useOrgSwitcherData } from 'bundles/enterprise-admin-navigation/hooks/useOrgSwitcherData';
import { ASK_COURSERA_PATH, HOME_PATH, SETTINGS_PATH } from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import withManagedOrganizations from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';
import CourseraLogoSvg from 'bundles/page/components/CourseraLogoSvg';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  logo: css`
    width: 139px;
    height: 20px;
  `,
  bottomContainer: css`
    display: flex;
    flex-direction: column;
  `,
  settingsList: css`
    list-style: none;
    padding: 0 var(--cds-spacing-200);
  `,
  profilePillAnchor: css`
    position: relative;
  `,
};

type PropsFromCaller = {
  thirdPartyOrganization: AdminDashboardOrg;
  adminRoleType?: string;
  toggleCoach: () => void;
};

type PropsFromOrganizations = {
  thirdPartyOrganizations: AdminDashboardOrg[];
};

type Props = PropsFromCaller & PropsFromOrganizations;

type Context = {
  enableSkillsPlanningForOrg: boolean;
  enableNewLearnersPage: boolean;
};

const EnterpriseAdminSideNavigation = (
  { thirdPartyOrganization, thirdPartyOrganizations, adminRoleType, toggleCoach }: Props,
  context: Context
) => {
  useViewAdminDashboardPageTracker(thirdPartyOrganization, adminRoleType);

  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const profilePillRef = React.useRef<HTMLDivElement>(null);

  const togglePopup = React.useCallback<() => void>(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  const closePopup = React.useCallback<() => void>(() => {
    setIsPopupOpen(false);
  }, []);

  const { sideNavTopItems, sideNavBottomItems } = useAdminSideNavItems({
    thirdPartyOrganization,
    enterpriseAdminFeatureFlags: context,
  });

  const shouldShowAskCoursera = useShouldShowAskCoursera(thirdPartyOrganization.slug);
  const isAdminCoachV2Enabled = useIsAdminCoachV2Enabled();
  const router = useRouter();

  const askCourseraHref = formatPattern(ASK_COURSERA_PATH, router.params);
  const isAskCourseraActive = router.isActive(askCourseraHref);

  // Ask Coursera lives in the component (not the declarative config) because its behavior
  // depends on the AdminCoach V2 product feature toggle: when enabled, it routes to the
  // ask-coursera page; otherwise it opens the inline coach panel. Appended after the link items.
  const topItemsToDisplay = React.useMemo<SideNavItem[]>(() => {
    if (!shouldShowAskCoursera) return sideNavTopItems;
    const askCourseraItem: SideNavItem = isAdminCoachV2Enabled
      ? {
          id: 'ASK_COURSERA',
          label: _t('Admin AI'),
          icon: AIGenerateBrandedIcon,
          href: askCourseraHref,
          isActive: isAskCourseraActive,
        }
      : {
          id: 'ASK_COURSERA',
          label: _t('Ask Coursera'),
          icon: AIGenerateBrandedIcon,
          onClick: toggleCoach,
        };
    return [...sideNavTopItems, askCourseraItem];
  }, [
    sideNavTopItems,
    shouldShowAskCoursera,
    toggleCoach,
    isAdminCoachV2Enabled,
    askCourseraHref,
    isAskCourseraActive,
  ]);

  // When inside the settings section (`o/<org-slug>/admin/settings`), the main nav list
  // swaps to the settings items; everywhere else under `/admin` it shows the standard list.
  const settingsItems = useSettingsSideNavItems();
  const isSettingsSection = router.isActive(formatPattern(SETTINGS_PATH, router.params));
  const itemsToDisplay = isSettingsSection ? settingsItems : topItemsToDisplay;

  // The settings section replaces the bottom Settings link with a "Back to Home"
  // link so admins have an obvious way out of the settings sub-navigation.
  const bottomItemsToDisplay = React.useMemo<SideNavItem[]>(() => {
    if (!isSettingsSection) return sideNavBottomItems;
    return [
      {
        id: 'BACK_TO_HOME',
        label: _t('Back to Home'),
        icon: ArrowPreviousIcon,
        href: formatPattern(HOME_PATH, router.params),
      },
    ];
  }, [isSettingsSection, sideNavBottomItems, router.params]);

  const { contexts } = useOrgSwitcherData(thirdPartyOrganization, thirdPartyOrganizations);

  const bottomContent = React.useMemo(
    () => (
      <div css={styles.bottomContainer}>
        {bottomItemsToDisplay.length > 0 && (
          <ul css={styles.settingsList}>
            {bottomItemsToDisplay.map((item) => (
              <SideNavItemComponent key={item.id} item={item} />
            ))}
          </ul>
        )}
        <div css={styles.profilePillAnchor}>
          <div ref={profilePillRef}>
            <AdminProfilePill
              thirdPartyOrganization={thirdPartyOrganization}
              isOpen={isPopupOpen}
              onClick={togglePopup}
            />
          </div>
          <AdminOrgSwitcherPopup
            isOpen={isPopupOpen}
            onClose={closePopup}
            thirdPartyOrganization={thirdPartyOrganization}
            contexts={contexts}
            triggerRef={profilePillRef}
          />
        </div>
      </div>
    ),
    [bottomItemsToDisplay, thirdPartyOrganization, isPopupOpen, togglePopup, closePopup, contexts]
  );

  return (
    <SideNav
      ariaLabel={_t('Admin navigation')}
      widths={{
        expandedPx: ADMIN_SIDENAV_WIDTH_EXPANDED_PX,
        collapsedPx: ADMIN_SIDENAV_WIDTH_COLLAPSED_PX,
      }}
      logo={<CourseraLogoSvg css={styles.logo} />}
      items={itemsToDisplay}
      bottom={bottomContent}
    />
  );
};

EnterpriseAdminSideNavigation.contextTypes = {
  enableSkillsPlanningForOrg: PropTypes.bool,
  enableNewLearnersPage: PropTypes.bool,
};

export default withManagedOrganizations<Props, PropsFromCaller>(EnterpriseAdminSideNavigation);
